/**
 * Iter84 (2026-02) — Payroll Trash page for Super Admin.
 *
 * Lists every soft-deleted payroll period across all modules and
 * offers a Restore action per row. Purge (permanent hard delete)
 * appears only for rows whose soft-delete is at least 30 days old
 * (`purge_eligible=true` from the backend).
 *
 * Route: `/admin/payroll-trash`
 * Backend: `GET /api/payroll/periods/trash`
 *          `POST /api/payroll/periods/{id}/restore`
 *          `POST /api/payroll/periods/{id}/purge`
 */
import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { Trash2, RotateCcw, AlertTriangle, Shield } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

export default function AdminPayrollTrash() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "kaafi_system_admin";
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [cooldownDays, setCooldownDays] = useState(30);
    const [restoreTarget, setRestoreTarget] = useState(null);
    const [restoreReason, setRestoreReason] = useState("");
    const [restoring, setRestoring] = useState(false);
    const [purgeTarget, setPurgeTarget] = useState(null);
    const [purgeReason, setPurgeReason] = useState("");
    const [purgePhrase, setPurgePhrase] = useState("");
    const [purging, setPurging] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/payroll/periods/trash");
            setItems(data?.items || []);
            setCooldownDays(data?.purge_cooldown_days || 30);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isSuperAdmin) load();
    }, [isSuperAdmin]);

    const doRestore = async () => {
        if (!restoreTarget) return;
        setRestoring(true);
        try {
            await api.post(
                `/payroll/periods/${restoreTarget.id}/restore`,
                { reason: restoreReason.trim() },
            );
            toast.success(
                `Restored "${restoreTarget.name}" to status "${restoreTarget.pre_delete_status}".`,
            );
            setRestoreTarget(null);
            setRestoreReason("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRestoring(false);
        }
    };

    const doPurge = async () => {
        if (!purgeTarget) return;
        setPurging(true);
        try {
            const { data } = await api.post(
                `/payroll/periods/${purgeTarget.id}/purge`,
                {
                    reason: purgeReason.trim(),
                    confirmation: purgePhrase,
                },
            );
            toast.success(
                `Purged permanently. Removed ${data?.cascaded_lines || 0} lines.`,
            );
            setPurgeTarget(null);
            setPurgeReason("");
            setPurgePhrase("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setPurging(false);
        }
    };

    if (!isSuperAdmin) {
        return (
            <AppShell title="Payroll Trash">
                <div
                    className="asl-card p-6 max-w-2xl"
                    data-testid="admin-payroll-trash-403"
                >
                    <Shield className="h-6 w-6 asl-crimson-text mb-2" />
                    <h2 className="text-lg font-semibold asl-navy-text">
                        Super Admin only
                    </h2>
                    <p className="text-sm mt-1 text-[color:var(--asl-muted)]">
                        Only the Kaafi System Admin can view the payroll trash.
                        Restoring a soft-deleted period is a privileged action —
                        contact your system administrator if you need a period
                        recovered.
                    </p>
                </div>
            </AppShell>
        );
    }

    return (
        <AppShell title="Payroll Trash">
            <section
                className="space-y-4"
                data-testid="admin-payroll-trash-page"
            >
                <header className="flex items-start gap-3">
                    <Trash2 className="h-6 w-6 asl-crimson-text" />
                    <div>
                        <h2 className="text-xl font-semibold asl-navy-text">
                            Payroll Trash
                        </h2>
                        <p className="text-sm text-[color:var(--asl-muted)]">
                            Soft-deleted payroll periods across every module.
                            Restore returns the period + its lines to the state
                            they were in before delete (Iter84). Permanent purge
                            is available only after {cooldownDays} days.
                        </p>
                    </div>
                </header>

                {loading && (
                    <div className="asl-card p-6 text-sm">Loading trash…</div>
                )}

                {!loading && items.length === 0 && (
                    <div
                        className="asl-card p-6 text-sm text-[color:var(--asl-muted)]"
                        data-testid="admin-payroll-trash-empty"
                    >
                        Trash is empty — no soft-deleted payroll periods.
                    </div>
                )}

                {!loading && items.length > 0 && (
                    <div className="asl-card p-0 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-[color:var(--asl-bg-alt,#F5F6F8)] text-left">
                                <tr>
                                    <th className="px-3 py-2">Module</th>
                                    <th className="px-3 py-2">Period</th>
                                    <th className="px-3 py-2">Pre-delete status</th>
                                    <th className="px-3 py-2">Deleted at</th>
                                    <th className="px-3 py-2">Reason</th>
                                    <th className="px-3 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((i) => (
                                    <tr
                                        key={i.id}
                                        className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                        data-testid={`admin-payroll-trash-row-${i.id}`}
                                    >
                                        <td className="px-3 py-2">
                                            <div className="mono text-xs asl-navy-text">
                                                {i.module_code}
                                            </div>
                                            <div className="text-xs text-[color:var(--asl-muted)]">
                                                {i.module_name}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="font-semibold text-xs asl-navy-text">
                                                {i.name}
                                            </div>
                                            <div className="text-xs text-[color:var(--asl-muted)]">
                                                {i.start_date} → {i.end_date}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <Badge variant="outline" className="mono text-[10px]">
                                                {i.pre_delete_status}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="text-xs mono">
                                                {i.deleted_at?.slice(0, 19).replace("T", " ")}
                                            </div>
                                            <div className="text-[10px] text-[color:var(--asl-muted)]">
                                                {i.age_days} day{i.age_days === 1 ? "" : "s"} ago
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 max-w-xs">
                                            <div
                                                className="text-xs text-[color:var(--asl-muted)] truncate"
                                                title={i.deleted_reason}
                                            >
                                                {i.deleted_reason || "—"}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setRestoreTarget(i);
                                                        setRestoreReason("");
                                                    }}
                                                    data-testid={`iter84-restore-btn-${i.id}`}
                                                    className="iter84-restore-btn"
                                                >
                                                    <RotateCcw className="h-3 w-3 mr-1" />
                                                    Restore
                                                </Button>
                                                {i.purge_eligible && (
                                                    <Button
                                                        size="sm"
                                                        className="bg-red-600 hover:bg-red-700 text-white"
                                                        onClick={() => {
                                                            setPurgeTarget(i);
                                                            setPurgeReason("");
                                                            setPurgePhrase("");
                                                        }}
                                                        data-testid={`iter84-purge-btn-${i.id}`}
                                                    >
                                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                                        Purge…
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Restore dialog */}
            <Dialog
                open={!!restoreTarget}
                onOpenChange={(o) => { if (!o) setRestoreTarget(null); }}
            >
                <DialogContent className="max-w-md" data-testid="iter84-restore-dialog">
                    <DialogHeader>
                        <DialogTitle>Restore this period?</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-3 text-xs">
                            <div className="font-semibold asl-navy-text">
                                {restoreTarget?.name}
                            </div>
                            <div className="text-[color:var(--asl-muted)] mt-0.5">
                                {restoreTarget?.module_code} — {restoreTarget?.start_date} → {restoreTarget?.end_date}
                            </div>
                            <div className="mt-2 text-[color:var(--asl-muted)]">
                                Restoring will put this period back at status{" "}
                                <Badge variant="outline" className="mono text-[10px]">
                                    {restoreTarget?.pre_delete_status}
                                </Badge>
                                {" "}and re-enable its payroll lines in exports + dashboards.
                            </div>
                        </div>
                        <label className="block">
                            <span className="text-xs text-[color:var(--asl-muted)]">
                                Reason (required, 3–400 chars):
                            </span>
                            <textarea
                                value={restoreReason}
                                onChange={(e) => setRestoreReason(e.target.value)}
                                rows={3}
                                maxLength={400}
                                className="mt-1 w-full border rounded-md px-2 py-1 text-sm"
                                data-testid="iter84-restore-reason"
                                placeholder="e.g. Restored after ops review — original delete was in error"
                                autoFocus
                            />
                        </label>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setRestoreTarget(null)}
                            disabled={restoring}
                            data-testid="iter84-restore-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="asl-navy-bg text-white"
                            onClick={doRestore}
                            disabled={restoring || restoreReason.trim().length < 3}
                            data-testid="iter84-restore-confirm"
                        >
                            {restoring ? "Restoring…" : "Restore"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Purge dialog */}
            <Dialog
                open={!!purgeTarget}
                onOpenChange={(o) => { if (!o) setPurgeTarget(null); }}
            >
                <DialogContent className="max-w-md" data-testid="iter84-purge-dialog">
                    <DialogHeader>
                        <DialogTitle>Purge permanently?</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900">
                            <div className="font-semibold">
                                This action is IRREVERSIBLE.
                            </div>
                            <ul className="list-disc ml-4 mt-1 space-y-0.5">
                                <li>Physically deletes the period document.</li>
                                <li>Physically deletes all payroll_lines for this period.</li>
                                <li>Physically deletes all payroll_funding rows.</li>
                                <li>Cannot be recovered — no undo.</li>
                            </ul>
                        </div>
                        <label className="block">
                            <span className="text-xs text-[color:var(--asl-muted)]">
                                Type <strong className="mono">PURGE PERMANENTLY</strong>:
                            </span>
                            <input
                                type="text"
                                value={purgePhrase}
                                onChange={(e) => setPurgePhrase(e.target.value)}
                                className="mt-1 w-full border rounded-md px-2 py-1 text-sm mono"
                                data-testid="iter84-purge-phrase"
                                autoFocus
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs text-[color:var(--asl-muted)]">
                                Reason (required, 3–400 chars):
                            </span>
                            <textarea
                                value={purgeReason}
                                onChange={(e) => setPurgeReason(e.target.value)}
                                rows={3}
                                maxLength={400}
                                className="mt-1 w-full border rounded-md px-2 py-1 text-sm"
                                data-testid="iter84-purge-reason"
                                placeholder="e.g. Compliance retention removal per legal review"
                            />
                        </label>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setPurgeTarget(null)}
                            disabled={purging}
                            data-testid="iter84-purge-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={doPurge}
                            disabled={
                                purging
                                || purgePhrase !== "PURGE PERMANENTLY"
                                || purgeReason.trim().length < 3
                            }
                            data-testid="iter84-purge-confirm"
                        >
                            {purging ? "Purging…" : "Purge permanently"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
